import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { mutate } from 'swr';
import {
    CameraCreationStatus,
    CameraCreationItem,
    CameraValidationStatus,
} from '@/types/source-creation';
import { useSourceCreationStore } from '@/stores/source-creation.store';
import { EngineService } from '@/services/engine';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { revalidateSourceFolders } from '@/hooks/useSourceFoldersInfinite';
import { getApiErrorMessage } from '@/utils/error-messages';
import { toast } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { revalidateOrganizationConfiguration } from '@/features/SettingsView/hooks/useConfiguration';
import { revalidateSourceList } from './useSourceListInfinite';

const MAX_CONCURRENT_REQUESTS = 3;

const isActiveStatus = (status: CameraCreationStatus) =>
    status === CameraCreationStatus.CREATING;

const isPendingStatus = (status: CameraCreationStatus) =>
    status === CameraCreationStatus.PENDING;

export function useCameraCreation() {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const cameraItems = useSourceCreationStore((state) => state.cameraItems);
    const updateCameraItem = useSourceCreationStore(
        (state) => state.updateCameraItem,
    );
    const processingRef = useRef(new Set<string>());

    useEffect(() => {
        const createCamera = async (item: CameraCreationItem) => {
            updateCameraItem(item.id, {
                status: CameraCreationStatus.CREATING,
            });
            const response = await EngineService.createCameras([
                {
                    name: item.name,
                    streamUrl: item.streamUrl,
                    username: item.username || '',
                    password: item.password || '',
                    faceRecognition: item.faceRecognition,
                    licensePlateRecognition: item.licensePlateRecognition,
                    opticalCharacterRecognition:
                        item.opticalCharacterRecognition,
                    analysisFramesPerSecond: item.analysisFramesPerSecond,
                },
            ]);
            return response[0];
        };

        const addCameraToFolder = async (
            folderId: string,
            sourceId: string,
        ) => {
            try {
                await EngineService.addSourceFolderSources(folderId, {
                    sourceIds: [sourceId],
                });
                return true;
            } catch (error) {
                console.warn(
                    `Failed to add camera source ${sourceId} to folder ${folderId}`,
                    error,
                );
                toast.error(t('toast.cameraAddToFolderError'));
                return false;
            }
        };

        const refreshFolderCaches = async (folderId: string) => {
            try {
                await Promise.all([
                    revalidateSourceFolders(),
                    mutate(
                        (key) =>
                            Array.isArray(key) &&
                            key[0] === CACHE_KEYS.SOURCE_FOLDER_CHILDREN &&
                            key[1] === folderId,
                    ),
                ]);
            } catch (error) {
                console.warn(
                    `Failed to refresh caches for folder ${folderId}`,
                    error,
                );
            }
        };

        const processCamera = async (item: CameraCreationItem) => {
            if (processingRef.current.has(item.id)) return;
            processingRef.current.add(item.id);
            try {
                const createdCamera = await createCamera(item);
                updateCameraItem(item.id, {
                    status: CameraCreationStatus.COMPLETED,
                    createdCameraId: createdCamera.id,
                    createdSourceId: createdCamera.sourceId,
                    validationStatus: CameraValidationStatus.VALIDATING,
                });

                if (item.targetFolderId) {
                    const wasAddedToFolder = await addCameraToFolder(
                        item.targetFolderId,
                        createdCamera.sourceId,
                    );
                    if (wasAddedToFolder) {
                        await refreshFolderCaches(item.targetFolderId);
                    }
                }

                try {
                    const validationResult =
                        await EngineService.validateCameraBySourceId(
                            createdCamera.sourceId,
                        );

                    if (validationResult.isValid) {
                        updateCameraItem(item.id, {
                            validationStatus: CameraValidationStatus.SUCCESS,
                            validationSnapshot: validationResult.snapshotBase64,
                        });
                    } else {
                        updateCameraItem(item.id, {
                            validationStatus: CameraValidationStatus.ERROR,
                            validationErrorType: validationResult.errorType,
                        });
                    }
                } catch {
                    updateCameraItem(item.id, {
                        validationStatus: CameraValidationStatus.ERROR,
                        validationErrorType: 'validation_failed',
                    });
                }

                void revalidateSourceList();
                mutate(
                    (key) =>
                        Array.isArray(key) &&
                        (key[0] === CACHE_KEYS.CAMERAS ||
                            key[0] === CACHE_KEYS.SOURCES_PREFIX),
                );
                revalidateOrganizationConfiguration();
            } catch (error: any) {
                updateCameraItem(item.id, {
                    status: CameraCreationStatus.ERROR,
                    error: getApiErrorMessage(
                        error,
                        t('statusDrawer.cameraCreateError'),
                    ),
                });
            } finally {
                processingRef.current.delete(item.id);
            }
        };

        const processPendingCameras = () => {
            const pendingItems = cameraItems.filter((item) =>
                isPendingStatus(item.status),
            );
            if (pendingItems.length === 0) return;
            const activeCount = cameraItems.filter((item) =>
                isActiveStatus(item.status),
            ).length;
            const availableSlots = MAX_CONCURRENT_REQUESTS - activeCount;
            if (availableSlots <= 0) return;
            const batch = pendingItems.slice(0, availableSlots);
            batch.forEach(processCamera);
        };

        processPendingCameras();
    }, [cameraItems, updateCameraItem]);
}
